import React, { useRef } from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { FileExcel } from 'react-bootstrap-icons';
import { Download } from "@mui/icons-material";
import ExcelJS from "exceljs";
import html2canvas from "html2canvas";

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#82d084'];

const LineChartCustom = ({ data, namex, seriesy, title }) => {
  const chartRef = useRef(null);

  // Filtrar series que no deberían graficarse
  const validSeries = seriesy?.filter(serie => {
    // Excluir campos que son metadatos, no datos numéricos
    const excludedKeys = ['Titulo', 'Fecha', 'titulo', 'fecha'];
    return !excludedKeys.includes(serie.dataKey);
  }) || [];

  const plotData = validSeries.map((serie, index) => ({
    type: 'scatter',
    mode: 'lines+markers',
    name: serie.label,
    x: data.map(item => item[namex]),
    y: data.map(item => item[serie.dataKey]),
    line: {
      color: serie.color || COLORS[index % COLORS.length],
      width: 3
    },
    marker: {
      size: 8,
      color: serie.color || COLORS[index % COLORS.length],
      line: {
        color: '#ffffff',
        width: 1
      }
    },
    hovertemplate: '<b>' + serie.label + '</b><br>' +
                   namex + ': %{x}<br>' +
                   'Valor: %{y:,.0f}<br>' +
                   '<extra></extra>',
  }));

  const layout = {
    title: {
      text: '',
      font: { size: 18, color: '#344767' }
    },
    xaxis: {
      title: namex || 'Eje X',
      showgrid: true,
      gridcolor: '#e0e0e0'
    },
    yaxis: {
      title: 'Valores',
      showgrid: true,
      gridcolor: '#e0e0e0'
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(250,250,250,1)',
    showlegend: true,
    legend: {
      orientation: 'h',
      x: 0.5,
      xanchor: 'center',
      y: -0.2,
      yanchor: 'top'
    },
    margin: { l: 60, r: 50, t: 40, b: 80 },
    height: 450,
    hovermode: 'x unified'
  };

  const config = {
    displayModeBar: false,
    displaylogo: false,
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    const headers = [namex, ...validSeries.map(s => s.label)];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    data.forEach((item) => {
      const row = [item[namex], ...validSeries.map(s => item[s.dataKey])];
      worksheet.addRow(row);
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  const handleDownloadPNG = async () => {
    if (chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const link = document.createElement('a');
        link.download = `${title || 'grafico'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error al descargar el gráfico:', error);
      }
    }
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Download />}
            onClick={handleDownloadPNG}
          >
            PNG
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox 
        sx={{ 
          backgroundColor: 'white', 
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        ref={chartRef}
      >
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </MDBox>
    </MDBox>
  );
};

export default LineChartCustom;
